import { useState, ViewTransition } from "react";
import * as stylex from "@stylexjs/stylex";

import TechBadge from "../badge/TechBadge";
import { PROJECTS } from "../data/placeholder";

function Project() {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div {...stylex.props(styles.page)}>
      <div {...stylex.props(styles.container)}>
        <ViewTransition>
          <div {...stylex.props(styles.header)}>
            <h1 {...stylex.props(styles.title, styles.titleGradient)}>
              Projects
            </h1>
            <p {...stylex.props(styles.subtitle)}>
              A collection of my recent work and personal projects that showcase
              my skills and passion for building great software.
            </p>
          </div>
        </ViewTransition>

        <ViewTransition>
          <div {...stylex.props(styles.projectGrid)}>
            {PROJECTS.map((project) => (
              <ViewTransition key={project.id} name={`project-${project.id}`}>
                <article
                  {...stylex.props(
                    styles.projectCard,
                    hoveredCard === project.id && styles.projectCardHovered,
                  )}
                  onMouseEnter={() => setHoveredCard(project.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div {...stylex.props(styles.imageContainer)}>
                    <img
                      src={project.image}
                      alt={project.title}
                      {...stylex.props(styles.projectImage)}
                    />
                    <div {...stylex.props(styles.imageOverlay)}>
                      <div {...stylex.props(styles.projectLinks)}>
                        {project.github && (
                          <a
                            href={project.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...stylex.props(styles.projectLink)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GitHubIcon />
                          </a>
                        )}
                        {project.demo && (
                          <a
                            href={project.demo}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...stylex.props(styles.projectLink)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLinkIcon />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div {...stylex.props(styles.cardContent)}>
                    <h2 {...stylex.props(styles.projectTitle)}>
                      {project.title}
                    </h2>
                    <p {...stylex.props(styles.projectDescription)}>
                      {project.description}
                    </p>
                    <div {...stylex.props(styles.techStack)}>
                      {project.tech.map((tech, i) => (
                        <TechBadge key={i}>{tech}</TechBadge>
                      ))}
                    </div>
                  </div>
                </article>
              </ViewTransition>
            ))}
          </div>
        </ViewTransition>
      </div>
    </div>
  );
}

function GitHubIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export default Project;

const fadeIn = stylex.keyframes({
  "0%": { opacity: 0, transform: "translateY(20px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const styles = stylex.create({
  page: {
    backgroundAttachment: "fixed",
    backgroundImage: "var(--bg-gradient)",
    minHeight: "100vh",
    width: "100%",
  },
  container: {
    gap: "3rem",
    marginInline: "auto",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    maxWidth: "1200px",
    paddingBottom: "4rem",
    paddingLeft: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingRight: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingTop: "4rem",
    width: "100%",
  },
  header: {
    animationDuration: "0.8s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    textAlign: "center",
    marginBottom: "1rem",
  },
  title: {
    color: "var(--primary-text)",
    display: "block",
    fontSize: {
      default: "3.5rem",
      "@media (max-width: 768px)": "2.25rem",
    },
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.3,
    position: "relative",
    marginBottom: "1rem",
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  titleGradient: {
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundImage: "var(--primary-gradient)",
  },
  subtitle: {
    marginInline: "auto",
    color: "var(--text-secondary)",
    fontSize: "1.1rem",
    lineHeight: 1.6,
    marginBottom: 0,
    marginTop: 0,
    maxWidth: "600px",
  },
  projectGrid: {
    gap: "2rem",
    animationDelay: "0.2s",
    animationDuration: "0.6s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    display: "grid",
    gridTemplateColumns: {
      default: "repeat(2, 1fr)",
      "@media (max-width: 900px)": "1fr",
    },
  },
  projectCard: {
    borderColor: "var(--media-inner-border)",
    borderRadius: "var(--card-corner-radius)",
    borderStyle: "solid",
    borderWidth: "1px",
    overflow: "hidden",
    transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    backdropFilter: "blur(20px)",
    backgroundColor: "var(--surface-glass)",
    boxShadow: "var(--shadow-sm)",
    display: "flex",
    flexDirection: "column",
  },
  projectCardHovered: {
    borderColor: "var(--glow-border)",
    boxShadow: "var(--shadow-neon)",
    transform: "translateY(-4px) scale(1.01)",
  },
  imageContainer: {
    overflow: "hidden",
    position: "relative",
    height: "200px",
  },
  projectImage: {
    transition: "transform 0.4s ease",
    objectFit: "cover",
    height: "100%",
    width: "100%",
  },
  imageOverlay: {
    transition: "opacity 0.3s ease",
    alignItems: "center",
    backgroundImage: "linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)",
    display: "flex",
    justifyContent: "center",
    opacity: {
      default: 0,
      ":hover": 1,
    },
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  projectLinks: {
    gap: "1rem",
    display: "flex",
  },
  projectLink: {
    padding: "0.75rem",
    borderRadius: "50%",
    transition: "all 0.2s ease",
    alignItems: "center",
    backdropFilter: "blur(10px)",
    backgroundColor: {
      default: "rgba(255, 255, 255, 0.1)",
      ":hover": "var(--primary-accent)",
    },
    color: "#ffffff",
    display: "flex",
    justifyContent: "center",
    transform: {
      default: null,
      ":hover": "scale(1.1)",
    },
  },
  cardContent: {
    padding: "1.5rem",
    gap: "1rem",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  projectTitle: {
    color: "var(--primary-text)",
    fontSize: "1.4rem",
    fontWeight: 700,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  projectDescription: {
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 3,
    color: "var(--text-secondary)",
    display: "-webkit-box",
    fontSize: "0.95rem",
    lineHeight: 1.6,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  techStack: {
    gap: "0.5rem",
    display: "flex",
    flexWrap: "wrap",
    marginTop: "auto",
  },
});
